import React, { useState } from 'react';
import { styled, Box, Typography } from '@mui/material';
import { FormControlLabel, Checkbox } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useToggle } from 'hooks/forms';
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { useNotification } from 'context/notification';
import { acceptTermsOfService } from 'pages/stripeOnboarding/StripeOnboardingAPI';

export default function ServiceAgreement(props) {
    const { hostId, connectId, business, representative, persons, bankAccounts, onUpdate } = props;

    const { businessType } = business;

    let directors = [];

    if (businessType === 'company') {
        directors = persons.filter((p) => p.director === true || p.owner === true);
    }
    if (businessType === 'non_profit') {
        directors = persons.filter((p) => p.director === true);
    }

    const [loading, setLoading] = useState(false);

    const agreement = useToggle();

    const { notify } = useNotification();

    const businessComplete = business.complete;
    const representativeComplete = Boolean(representative);
    const directorsComplete = Boolean(directors.length > 0);
    const bankingComplete = Boolean(bankAccounts.length > 0);

    const setupComplete =
        businessComplete && representativeComplete && directorsComplete && bankingComplete;

    const handleSubmit = () => {
        setLoading(true);

        acceptTermsOfService(hostId, connectId)
            .then(({ business, requirements }) => {
                notify.success('Service Agreement Accepted');
                onUpdate(business, requirements);
            })
            .catch((error) => {
                notify.error(error.message);
                setLoading(false);
            });
    };

    return (
        <Section>
            <Box mb={2}>
                <Header>Stripe Service Agreement</Header>
            </Box>
            {setupComplete === true ? (
                <Typography>
                    We use Stripe to provide our payment processing services. By registering your
                    account, you agree to the Stripe Connected Account Agreement. You also
                    acknowledge that Stripe can process your data as explained in the{' '}
                    <Link
                        href="https://stripe.com/en-ca/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Stripe Privacy Policy
                    </Link>
                    . Stripe can also obtain information from credit agencies to verify your
                    identity. That information will be used for the purposes described in their
                    Privacy Policy.
                </Typography>
            ) : (
                <>
                    <Typography>
                        You must complete your account details before accepting the service
                        agreement.
                    </Typography>
                    <Box display={'flex'} alignItems={'center'} mb={2} mt={4}>
                        {businessComplete ? <CompleteIcon /> : <IncompleteIcon />}
                        <Typography>Business Details</Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        {representativeComplete ? <CompleteIcon /> : <IncompleteIcon />}
                        <Typography>Account Representative</Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        {directorsComplete ? <CompleteIcon /> : <IncompleteIcon />}
                        {businessType === 'company' && (
                            <Typography>Management and Ownership</Typography>
                        )}
                        {businessType === 'non_profit' && (
                            <Typography>Board of Directors</Typography>
                        )}
                    </Box>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        {bankingComplete ? <CompleteIcon /> : <IncompleteIcon />}
                        <Typography>Bank Accounts</Typography>
                    </Box>
                </>
            )}
            <Content>
                <FormControlLabel
                    disabled={setupComplete === false}
                    control={<Checkbox {...agreement.formProps} />}
                    label={
                        <Typography>
                            I agree to the{' '}
                            <Link
                                href="https://stripe.com/en-ca/legal/connect-account"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Stripe Connected Account Agreement
                            </Link>
                        </Typography>
                    }
                />
                <Box display="flex" justifyContent="flex-end" mt={4}>
                    <SubmitButton
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={setupComplete === false || agreement.checked === false || loading}
                    >
                        Confirm
                    </SubmitButton>
                </Box>
            </Content>
        </Section>
    );
}

const Link = styled('a')(({ theme }) => ({
    color: theme.palette.info.dark,
    display: 'inline',
}));

const CompleteIcon = styled(CheckCircleIcon)(({ theme }) => ({
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
}));

const IncompleteIcon = styled(RadioButtonUncheckedIcon)(({ theme }) => ({
    color: theme.palette.grey[800],
    marginRight: theme.spacing(1),
}));
